import React, { useEffect, useMemo, useState } from "react";
import { Icon, Input, Popup } from "semantic-ui-react";
import "./YearPicker.css";

const YEARS_ON_PAGE = 12;

function getPageStartYear(year) {
  const y = Number(year) || new Date().getFullYear();
  return y - (y % YEARS_ON_PAGE);
}

/**
 * Lightweight year-only picker (Semantic UI React).
 * When `availableYears` is provided, only those years are selectable.
 */
function YearPicker({
  value,
  onChange,
  availableYears = [],
  minYear,
  maxYear,
  placeholder = "Year",
  id,
  name,
  className = "",
  disabled = false,
}) {
  const availableSet = useMemo(
    () => new Set((availableYears || []).map((y) => Number(y))),
    [availableYears],
  );
  const hasAvailableList = availableYears && availableYears.length > 0;

  const selectedYear = Number(value) || null;
  const min = hasAvailableList
    ? Math.min(...availableYears.map(Number))
    : Number(minYear);
  const max = hasAvailableList
    ? Math.max(...availableYears.map(Number))
    : Number(maxYear);

  const [open, setOpen] = useState(false);
  const [pageStart, setPageStart] = useState(() =>
    getPageStartYear(selectedYear || max || new Date().getFullYear()),
  );

  useEffect(() => {
    if (open) {
      setPageStart(
        getPageStartYear(selectedYear || max || new Date().getFullYear()),
      );
    }
  }, [open, selectedYear, max]);

  const years = useMemo(() => {
    return Array.from({ length: YEARS_ON_PAGE }, (_, i) => pageStart + i);
  }, [pageStart]);

  const pageEnd = pageStart + YEARS_ON_PAGE - 1;
  const canGoPrev = pageStart > min;
  const canGoNext = pageEnd < max;

  const isYearDisabled = (year) => {
    if (hasAvailableList) {
      return !availableSet.has(year);
    }
    return year < min || year > max;
  };

  const handleSelectYear = (year) => {
    if (isYearDisabled(year)) {
      return;
    }
    onChange(year);
    setOpen(false);
  };

  const calendar = (
    <div className="year-picker-calendar">
      <div className="year-picker-header">
        <button
          type="button"
          className="year-picker-nav"
          disabled={!canGoPrev}
          onClick={() => setPageStart((prev) => prev - YEARS_ON_PAGE)}
          aria-label="Previous years"
        >
          <Icon name="chevron left" />
        </button>
        <span className="year-picker-range">
          {pageStart} - {pageEnd}
        </span>
        <button
          type="button"
          className="year-picker-nav"
          disabled={!canGoNext}
          onClick={() => setPageStart((prev) => prev + YEARS_ON_PAGE)}
          aria-label="Next years"
        >
          <Icon name="chevron right" />
        </button>
      </div>
      <div className="year-picker-grid">
        {years.map((year) => {
          const yearDisabled = isYearDisabled(year);
          const selected = year === selectedYear;
          return (
            <button
              key={year}
              type="button"
              className={[
                "year-picker-cell",
                yearDisabled ? "disabled" : "",
                selected ? "selected" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              disabled={yearDisabled}
              onClick={() => handleSelectYear(year)}
            >
              {year}
            </button>
          );
        })}
      </div>
      {hasAvailableList && (
        <div className="year-picker-hint">
          Only years with critical units are selectable
        </div>
      )}
    </div>
  );

  const isLocked = disabled || !hasAvailableList;

  return (
    <Popup
      className="year-picker-popup"
      trigger={
        <Input
          id={id}
          name={name}
          className={`year-picker-input ${isLocked ? "is-locked" : ""} ${className}`.trim()}
          readOnly
          icon="calendar"
          iconPosition="right"
          placeholder={placeholder}
          value={value ? String(value) : ""}
          onClick={() => {
            if (!isLocked) {
              setOpen(true);
            }
          }}
        />
      }
      content={calendar}
      on="click"
      open={open && !isLocked}
      onOpen={() => {
        if (!isLocked) {
          setOpen(true);
        }
      }}
      onClose={() => setOpen(false)}
      position="bottom left"
      hideOnScroll
      disabled={isLocked}
    />
  );
}

export default YearPicker;
